import './FunctionV2.css'

import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ALL_FUNCTIONS_GROUP, FunctionValue } from '@makyoapp/makyo-common'
import { Fn16, StarFilled16 } from '@makyoapp/makyo-icon/icon'
import { Modal, Tabs } from '@makyoapp/makyo-ui'

import { cn } from '@/lib/cn'
import { colTypeToLabel } from '@/shared/ag-grid'
import { COLUMN_TYPES_BG_COLOR } from '@/shared/ag-grid-constants'

import { ColumnTypeIcon } from '../ColumnTypeIcon'
import { FnCategoriesDropdown } from './components/FnCategoriesDropdown'
import { FnDescription } from './components/FnDescription'
import { FnFields } from './components/FnFields'
import { FnFormat } from './components/FnFormat'
import { FunctionNavItem } from './components/FnNavItem'
import { DEFAULT_FAVORITE_FUNCTIONS } from './constants'
import { getCategoryItems, getDefaultCategory, getFnSyntax } from './helpers'
import { CategoryState } from './types'

interface FunctionV2Props {
  id?: string
  className?: string
  value?: FunctionValue
  onChange: (next: FunctionValue) => void
}

export default function FunctionV2({ id, className, value, onChange }: FunctionV2Props) {
  const { t } = useTranslation()

  // derived state
  const activeValue = ALL_FUNCTIONS_GROUP.find((f) => f.id === value?.item.id)

  // local state
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<CategoryState>(getDefaultCategory(activeValue))
  const [favorites, setFavorites] = useState<string[]>(DEFAULT_FAVORITE_FUNCTIONS)

  // derived State
  const categoryItems = getCategoryItems(category, favorites)

  const functionItems = useMemo(() => {
    const cat = getCategoryItems(category, favorites)
    // hide simple math category as it's already handled in the mth function
    const HIDDEN_FUNCTIONS = ['add', 'concat', 'divide', 'multiply', 'minus']
    return search
      ? cat
          .filter((c) => {
            const long = t(`fn.${c.id}.long`)
            const short = t(`fn.${c.id}.short`)
            return (
              !HIDDEN_FUNCTIONS.includes(c.id) &&
              `${c.title} ${short} ${long}`.toLowerCase().includes(search.toLowerCase())
            )
          })
          .map((c) => {
            let points = 0
            const long = t(`fn.${c.id}.long`)
            const short = t(`fn.${c.id}.short`)

            if (c.title.toLowerCase() === search.toLowerCase()) {
              points += 9
            }

            if (c.title.toLowerCase().includes(search.toLowerCase())) {
              points += 5
            }

            if (short.toLowerCase().includes(search.toLowerCase())) {
              points += 3
            }

            if (long.toLowerCase().includes(search.toLowerCase())) {
              points += 1
            }

            return { ...c, points }
          })
          .sort((a, b) => b.points - a.points)
      : categoryItems.filter((c) => !HIDDEN_FUNCTIONS.includes(c.id))
  }, [category, categoryItems, favorites, search, t])

  const activeTab = useMemo(() => {
    const currentActiveSameTab = functionItems.find((i) => i.id === value?.item.id)
    if (currentActiveSameTab) {
      return value?.item.id
    } else {
      return ''
    }
  }, [functionItems, value?.item.id])

  const handleSearch = (val = '') => {
    setSearch(val)
    if (val) {
      setCategory({ cat: 'all', subCat: undefined })
    }
  }

  const handleChangeCategory = ({ cat, subCat }: CategoryState) => {
    if (cat) {
      setCategory({ cat, subCat })

      if (functionItems.length > 0) {
        const fnItem = functionItems[0]
        const nextValue: Record<string, any> = {}
        for (const f of fnItem.fields) {
          if (f.defaultValue !== undefined) {
            nextValue[f.id] = f.defaultValue
          }
        }
        onChange({
          item: { id: fnItem.id, title: fnItem.title, fn: fnItem.fn },
          value: nextValue,
        })
      }
    }
  }

  return (
    <div id={id} className={cn(className, 'border bdr')}>
      {/* header */}
      <div className='flex items-center border-b bdr'>
        <div className='w-1/2 border-r bdr'>
          <Modal.Search
            className='w-full border-0!'
            placeholder={t('Search')}
            value={search}
            onChange={handleSearch}
            onClear={() => {
              setSearch('')
            }}
          />
        </div>

        <div className='h-10 w-1/2 shrink-0 flex items-center'>
          <FnCategoriesDropdown value={category} onChange={handleChangeCategory} />
        </div>
      </div>

      {/* body */}
      <Tabs className='h-[360px]' activeKey={activeTab} defaultActiveKey={undefined as unknown as string}>
        <div className='flex h-full'>
          <Tabs.Nav
            className='w-[140px] shrink-0 pt-2 bdr flex flex-col overflow-y-auto overflow-x-hidden h-full'
            style={{
              borderInlineEndWidth: '1px',
            }}
          >
            {functionItems.map((item) => {
              const isActive = value?.item.id === item.id
              return <FunctionNavItem onChange={onChange} key={item.id} item={item} isActive={isActive} />
            })}
          </Tabs.Nav>
          <div className='overflow-y-auto w-full h-full'>
            <Tabs.Panel unmountOnExit={true} mountOnEnter={true} className='w-full h-full' eventKey=''>
              <div className='flex-cc min-w-0 w-full h-full text-base'>
                <div className='text-center'>
                  <div className='font-bold mb-4'>Please Select Function</div>
                  <div className='w-full flex-cc min-w-0'>
                    <Fn16 width={64} height={64} className='opacity-50' />
                  </div>
                </div>
              </div>
            </Tabs.Panel>

            {functionItems.map((fnItem) => {
              const withFormat = !fnItem.noFormatting
              const syntax = getFnSyntax(fnItem)
              const isFavorite = favorites.includes(fnItem.id)

              return (
                <Tabs.Panel
                  unmountOnExit={true}
                  mountOnEnter={true}
                  className='w-full'
                  eventKey={fnItem.id}
                  key={fnItem.id}
                >
                  {fnItem.title && (
                    <div className='function-header'>
                      <div className='function-title'>
                        <span>{fnItem.title}</span>
                        <button
                          className={cn(
                            'function-favorite',
                            'text-zinc-300 dark:text-slate-500',
                            isFavorite && 'text-amber-500 dark:text-amber-500'
                          )}
                          onClick={() => {
                            if (isFavorite) {
                              setFavorites((prev) => prev.filter((f) => f !== fnItem.id))
                            } else {
                              setFavorites((prev) => [...prev, fnItem.id])
                            }
                          }}
                        >
                          <StarFilled16 />
                        </button>
                      </div>

                      {Boolean(fnItem.returnUnknown) ? (
                        <div className={cn('function-result', 'bg-black text-white!')}>
                          <Trans>unknown</Trans>
                        </div>
                      ) : (
                        <div
                          className={cn(
                            'function-result',
                            COLUMN_TYPES_BG_COLOR[fnItem.returnType || ColumnTypes.Text] || 'bg-black'
                          )}
                        >
                          <ColumnTypeIcon type={fnItem.returnType || ColumnTypes.Text} />
                          <div>{colTypeToLabel(fnItem.returnType || ColumnTypes.Text)}</div>
                        </div>
                      )}
                    </div>
                  )}

                  <FnDescription fnItem={fnItem} />

                  {syntax && (
                    <div className='function-syntax'>
                      <div className='function-syntax-title'>
                        <Trans>SYNTAX</Trans>
                      </div>
                      <div className='function-syntax-code'>{syntax}</div>
                    </div>
                  )}

                  <FnFields item={fnItem} value={value} onChange={onChange} />

                  {withFormat && (
                    <FnFormat
                      item={fnItem}
                      options={{
                        allowedDisplayOptions: fnItem.allowedDateTimeDisplay,
                        autoSelectDisplayOptions: !!fnItem.allowedDateTimeDisplay,
                      }}
                    />
                  )}
                </Tabs.Panel>
              )
            })}
          </div>
        </div>
      </Tabs>
    </div>
  )
}
